import { useState, useMemo } from 'react';
import { Badge, Box, Flex, Paper, Stack, Text } from '@mantine/core';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { TaskStatus } from '@dealstack/shared/lib/generated';
import type { BatchListItem } from '@dealstack/frontend/app/services/DocumentExtraction.service';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatBatchTimestamp } from './BatchList.utils';

const PAGE_SIZE = 5;

interface Props {
	batches: BatchListItem[];
	isLoading: boolean;
	onSelectBatch: (batchId: string) => void;
}

const statusColorMap: Record<TaskStatus, string> = {
	[TaskStatus.PENDING]: 'gray',
	[TaskStatus.IN_PROGRESS]: 'blue',
	[TaskStatus.DONE]: 'green',
	[TaskStatus.ERROR]: 'red',
	[TaskStatus.CANCELLED]: 'gray',
};

const statusLabelKeys: Record<TaskStatus, string> = {
	[TaskStatus.PENDING]: 'transactions.add_from_document.batch_status_pending',
	[TaskStatus.IN_PROGRESS]: 'transactions.add_from_document.batch_status_in_progress',
	[TaskStatus.DONE]: 'transactions.add_from_document.batch_status_complete',
	[TaskStatus.ERROR]: 'transactions.add_from_document.batch_status_error',
	[TaskStatus.CANCELLED]: 'transactions.add_from_document.batch_status_cancelled',
};

export const BatchList = ({ batches, isLoading, onSelectBatch }: Props) => {
	const [page, setPage] = useState(0);
	const tCapManage = useTranslationCapManage();

	const sorted = useMemo(
		() =>
			[...batches].sort(
				(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
			),
		[batches]
	);

	const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
	const paginated = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

	if (isLoading) {
		return (
			<div style={{ position: 'relative', minHeight: 100 }}>
				<InlineLoader withoutBackground />
			</div>
		);
	}

	if (batches.length === 0) {
		return (
			<Flex direction="column" align="center" justify="center" py="xl" gap="xs">
				<Icon variant="document-v3" size="xl" />
				<Text size="sm" color="#4F616C">
					{tCapManage('transactions.add_from_document.no_batches_yet')}
				</Text>
			</Flex>
		);
	}

	return (
		<Stack gap="xs">
			{paginated.map((batch) => (
				<Paper
					key={batch.id}
					p="sm"
					withBorder
					style={{
						cursor: 'pointer',
						borderColor: '#D3D8DA',
					}}
					sx={{
						'&:hover': {
							borderColor: '#0D5EF2',
							backgroundColor: '#F8F9FA',
						},
					}}
					onClick={() => onSelectBatch(batch.id)}
				>
					<Flex align="center" justify="space-between">
						<Flex align="center" gap="sm" style={{ flex: 1, minWidth: 0 }}>
							<Icon variant="document-v3" size="md" />
							<Flex direction="column" gap={2} style={{ minWidth: 0 }}>
								<Text size="sm" fw={500} lineClamp={1}>
									{batch.title ||
										tCapManage('transactions.add_from_document.untitled_batch')}
								</Text>
								<Text size="xs" color="#4F616C">
									{batch.id}
								</Text>
								<Text size="xs" color="#4F616C">
									{batch.documentCount === 1
										? tCapManage(
												'transactions.add_from_document.document_count_one',
												{ count: batch.documentCount }
											)
										: tCapManage(
												'transactions.add_from_document.document_count_other',
												{ count: batch.documentCount }
											)}{' '}
									&middot; {formatBatchTimestamp(batch.createdAt)}
								</Text>
							</Flex>
						</Flex>
						<Badge
							size="sm"
							color={statusColorMap[batch.status] || 'gray'}
							variant="light"
							styles={{
								root: {
									textTransform: 'none',
									fontWeight: 400,
								},
							}}
						>
							{tCapManage(statusLabelKeys[batch.status]) || batch.status}
						</Badge>
					</Flex>

					{batch.documents?.length > 0 && (
						<Box
							mt="xs"
							pl={36}
							style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}
						>
							{batch.documents.map((doc) => (
								<Box
									key={doc.id}
									mb={4}
									p={4}
									style={{
										backgroundColor: '#F8F9FA',
										borderRadius: 4,
										lineHeight: 1.4,
									}}
								>
									<Text size="xs" fw={500} mb={2}>
										{doc.fileName}
									</Text>
									<Text size="xs" color="#4F616C">
										id: {doc.id}
									</Text>
									<Text size="xs" color="#4F616C">
										workflowId: {doc.workflowId}
									</Text>
									<Text size="xs" color="#4F616C">
										status: {doc.status}
									</Text>
									{doc.errorMessage && (
										<Text size="xs" color="red">
											error: {doc.errorMessage}
										</Text>
									)}
								</Box>
							))}
						</Box>
					)}
				</Paper>
			))}

			{totalPages > 1 && (
				<Flex justify="center" align="center" gap="sm" mt="xs">
					<ButtonV2
						variant="ghost"
						size="xs"
						buttonTheme="light"
						onClick={() => setPage((p) => p - 1)}
						disabled={page === 0}
					>
						{tCapManage('transactions.add_from_document.previous')}
					</ButtonV2>
					<Text size="xs" color="#4F616C">
						{tCapManage('transactions.add_from_document.page_indicator', {
							current: page + 1,
							total: totalPages,
						})}
					</Text>
					<ButtonV2
						variant="ghost"
						size="xs"
						buttonTheme="light"
						onClick={() => setPage((p) => p + 1)}
						disabled={page >= totalPages - 1}
					>
						{tCapManage('transactions.add_from_document.next')}
					</ButtonV2>
				</Flex>
			)}
		</Stack>
	);
};
